// Who holds a number, and moving it — NumberInventoryController (/v1/numbers).
//
// This is a different table from the one /phones edits. `phone_numbers` drives
// inbound DID routing and is mirrored to the switches; `provider_numbers` holds
// OWNERSHIP, and it is what CallerIdAuthorizer and the outbound caller-ID
// picker read. Moving a number between organisations means writing the second,
// and it leaves the first — including `ivr_dialing_info` on the switch —
// exactly as it was.
//
// Two shapes, two casings, one feature: the number rows come back snake_case
// (`purchased_by_organization_id`) while the tenure rows come back camelCase
// (`organizationId`). That is what the server sends; it is not a choice made
// here, and binding to the wrong one yields a table of blanks.
import api from '../axios.config';
import { API_ENDPOINTS } from '../endpoints';
import { responseFrom, errorFrom } from '../apiEnvelope';
import type { APIResponse } from 'types/ivr';
import type { ProviderNumber } from './providers.service';

/** Committed term. The server's enum; MONTHLY is its default. */
export type LockInTerm = 'MONTHLY' | 'ANNUAL';

/**
 * One tenure of a number by an organisation — acquired, held, released.
 *
 * camelCase, and `organizationName` is the name AS AT ACQUISITION: organisations
 * get renamed and the history deliberately does not follow. `open` is derived
 * server-side from `releasedAt === null`.
 */
export interface NumberAllocation {
  id: string;
  providerNumberId: number;
  e164: string;
  organizationId: string;
  organizationName?: string | null;
  /** Epoch millis. */
  acquiredAt: number | string;
  /** Null while this is the current holder. */
  releasedAt: number | string | null;
  lockInMonths?: number | null;
  lockInEndsAt?: number | string | null;
  termsServed?: number;
  releasedEarly?: boolean;
  acquiredBy?: string | null;
  releasedBy?: string | null;
  notes?: string | null;
  createdAt?: number | string;
  open?: boolean;
}

export interface AcquireOptions {
  organizationId: string;
  term?: LockInTerm;
  autoRenew?: boolean;
  /**
   * A deliberate override of the current holder's commitment, never a retry.
   * Omitted unless the operator has been told what it does and chosen it.
   */
  force?: boolean;
}

export const numberOwnershipService = {
  /**
   * Every number in inventory, with its owner.
   *
   * There is no list endpoint on /v1/numbers — it exposes only
   * `held-by/{organizationId}`, which needs an organisation you already
   * suspect. The platform-wide list lives on the provider registry, and it
   * returns the ownership columns on every row, so it is the only way to answer
   * "who holds this number" without knowing the answer first.
   */
  list: async (params?: { providerId?: number; status?: string }): Promise<APIResponse<ProviderNumber[]>> => {
    try { return responseFrom<ProviderNumber[]>((await api.get(API_ENDPOINTS.PROVIDER_NUMBERS, { params })).data); }
    catch (e: any) { return errorFrom<ProviderNumber[]>(e, 'Could not load the number inventory'); }
  },

  /**
   * Assign a number to an organisation and start its term.
   *
   * Keyed on the numeric provider_numbers.id, NOT the E.164 — the two are
   * different identifiers and the path takes the id.
   *
   * The server refuses when the current holder is still inside their lock-in,
   * and when the target organisation already holds the number. Only the first
   * is overridable; `force` is checked on that branch alone.
   */
  acquire: async (numberId: number, opts: AcquireOptions): Promise<APIResponse<ProviderNumber>> => {
    try {
      const params: Record<string, any> = {
        organizationId: opts.organizationId,
        term: opts.term || 'MONTHLY',
        autoRenew: opts.autoRenew !== false,
      };
      // Sent only when chosen. A default-on override is not an override.
      if (opts.force) params.force = true;
      return responseFrom<ProviderNumber>((await api.post(API_ENDPOINTS.NUMBER_ACQUIRE(numberId), null, { params })).data);
    } catch (e: any) { return errorFrom<ProviderNumber>(e, 'Could not assign the number'); }
  },

  /**
   * Return a number to the pool.
   *
   * Ownership only. This does NOT remove the number from `ivr_dialing_info`, so
   * a released number keeps answering inbound calls on the switch exactly as it
   * did — that removal is a separate, deliberate step.
   */
  release: async (numberId: number, reason?: string, force?: boolean): Promise<APIResponse<ProviderNumber>> => {
    try {
      const params: Record<string, any> = {};
      if (reason && reason.trim()) params.reason = reason.trim();
      if (force) params.force = true;
      return responseFrom<ProviderNumber>((await api.post(API_ENDPOINTS.NUMBER_RELEASE(numberId), null, { params })).data);
    } catch (e: any) { return errorFrom<ProviderNumber>(e, 'Could not release the number'); }
  },

  /** Everyone who has ever held this number, keyed on the E.164 (not the id). */
  history: async (e164: string): Promise<APIResponse<NumberAllocation[]>> => {
    try { return responseFrom<NumberAllocation[]>((await api.get(API_ENDPOINTS.NUMBER_HISTORY(e164))).data); }
    catch (e: any) { return errorFrom<NumberAllocation[]>(e, 'Could not load this number’s history'); }
  },

  /** The open tenures an organisation holds. */
  heldBy: async (organizationId: string): Promise<APIResponse<NumberAllocation[]>> => {
    try { return responseFrom<NumberAllocation[]>((await api.get(API_ENDPOINTS.NUMBERS_HELD_BY(organizationId))).data); }
    catch (e: any) { return errorFrom<NumberAllocation[]>(e, 'Could not load the numbers this organisation holds'); }
  },
};

export default numberOwnershipService;
